/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useThrottledCallback } from "@react-hookz/web";
import { getClientId, useForkRef, useRequestAnimationFrame, useResizeObserver } from "@smartsheet/lodestar-utils";
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import ScrollLock from "react-scrolllock";
import { scrollbarClassNames } from "../../../constants/scrollbars";
import { Keyline, KEYLINE_WIDTH_PROPERTY_NAME } from "../styled/Keyline";
import { ScrollAreaScrollStyled } from "../styled/ScrollAreaScrollStyled";
import { ScrollAreaStyled } from "../styled/ScrollAreaStyled";
import { ScrollAreaGutter } from "../types";
import { getScrollInfo } from "../utils/scrollUtils";

export interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * A `clientId` prop is provided for specified elements, which is a unique string
     * that appears as a data attribute `data-client-id` in the rendered code, serving as
     * a hook for automated tests
     */
    clientId?: string;
    /**
     * gutter is the amount of padding/margin that should be applied to the StyledScrollAreaScroll/Keyline component.
     * Default is Gutter.Legacy
     */
    gutter?: ScrollAreaGutter;

    /**
     * set to true to lock the page from scrolling.  Useful for modal contexts.
     */
    lockPageScroll?: boolean;
    /**
     * Apply a specific scrollbar style.
     */
    scrollbarStyle?: keyof typeof scrollbarClassNames;
}
/**
 * ScrollArea is a component to handle a vertically scrolling content area. It is responsible for:
 * - Rendering the content area
 * - Providing a scrolling context for the content area
 * - Optionally locking browser scroll (useful for when being used inside a modal, so the page doesn't scroll behind it)
 * - Rendering an affordance that there's more content below the fold
 */
export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
    (
        { children, clientId, gutter = "legacy", lockPageScroll = false, scrollbarStyle, className, ...rest },
        providedRef
    ) => {
        const [showBottomKeyline, setShowBottomKeyline] = useState(false);
        const scrollContainerRef = useRef<HTMLDivElement | null>(null);
        const scrollAreaRootRef = useRef<HTMLDivElement | null>(null);
        const combinedRootRef = useForkRef(scrollAreaRootRef, providedRef);

        const { raf } = useRequestAnimationFrame();
        const determineKeylines = useCallback(() => {
            raf(() => {
                const scrollContainer = scrollContainerRef.current;
                const scrollAreaRoot = scrollAreaRootRef.current;
                if (!scrollContainer || !scrollAreaRoot) {
                    return;
                }

                const { top, viewportHeight, contentHeight } = getScrollInfo(scrollContainer);

                const scrollableDistance = contentHeight - viewportHeight;
                const showBottomKeyline = top < scrollableDistance;

                setShowBottomKeyline(showBottomKeyline);

                // Set the keyline's padding to the current width of the scrollContainer, minus it's left/right padding.
                const scrollContainerStyle = window.getComputedStyle(scrollContainer);
                const scrollContainerWidth =
                    scrollContainer.clientWidth -
                    parseFloat(scrollContainerStyle.paddingLeft) -
                    parseFloat(scrollContainerStyle.paddingRight);

                scrollAreaRoot.style.setProperty(KEYLINE_WIDTH_PROPERTY_NAME, `${scrollContainerWidth}px`);
            });
        }, [raf]);

        // Attach handlers to the scroll container's current element to show/hide keylines.
        useEffect(() => {
            const containerElement = scrollContainerRef.current;
            if (containerElement) {
                // Run this initially to determine if we need to show the keyline.
                determineKeylines();

                containerElement.addEventListener("scroll", determineKeylines, {
                    passive: true,
                });
                return () => {
                    containerElement.removeEventListener("scroll", determineKeylines);
                };
            }
        }, [determineKeylines]);

        // We also need to evaluate the keyline's visibility if the container resizes.
        const { ref: resizeObserverRef } = useResizeObserver(() => {
            const containerElement = scrollContainerRef.current;
            if (containerElement) {
                determineKeylines();
            }
        });

        const combinedRef = useForkRef(scrollContainerRef, resizeObserverRef);

        const timerId = useRef<NodeJS.Timeout | null>(null);

        // Fix for https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/589.
        // We need to quickly toggle the ScrollLock component off/on when the window resizes. This
        // causes the ScrollLock component to re-calculate the window height and update the CSS
        // properties it applies to the body when scrolling is locked.
        const [localScrollLockIsActive, setLocalScrollIsActive] = useState(lockPageScroll);
        const throttledHandleResize = useThrottledCallback(
            () => {
                setLocalScrollIsActive(false);
                if (timerId.current) {
                    clearTimeout(timerId.current);
                }
                timerId.current = setTimeout(() => {
                    setLocalScrollIsActive(true);
                });
            },
            [],
            30
        );
        useEffect(() => {
            window.addEventListener("resize", throttledHandleResize);
            return () => {
                window.removeEventListener("resize", throttledHandleResize);
                if (timerId.current) {
                    clearTimeout(timerId.current);
                }
            };
        }, [throttledHandleResize]);

        return (
            <ScrollAreaStyled className={className} {...rest} ref={combinedRootRef}>
                <ScrollAreaScrollStyled
                    data-client-id={clientId}
                    gutter={gutter}
                    ref={combinedRef}
                    className={scrollbarStyle && scrollbarClassNames[scrollbarStyle]}
                >
                    {children}
                    {lockPageScroll && <ScrollLock isActive={localScrollLockIsActive} />}
                </ScrollAreaScrollStyled>
                <Keyline
                    data-client-id={getClientId(clientId, "lodestar-keyline")}
                    isVisible={showBottomKeyline}
                    gutter={gutter}
                />
            </ScrollAreaStyled>
        );
    }
);

ScrollArea.displayName = "ScrollArea";
